// src/pages/UnifiedContract.js
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SignaturePad from '../components/SignaturePad';
import LoadingTransition from '../components/LoadingTransition';
import ContractViewer from '../components/ContractViewer';
import { PDFDocument, rgb } from 'pdf-lib';
import { pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import HeaderComponent from '../components/HeaderComponent';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Predefined coordinates for signature fields for each contract type
const SIGNATURE_COORDINATES = {
  employment: {
    employeeSignature: {
      page: 9,
      x: 81.0471,   
      y: 332.836,  
      width: 149,
      height: 50
    },
    witnessSignature: {
      page: 9,
      x: 363.05,
      y: 330.845,
      width: 140,
      height: 49
    },
    employeeName: {
      page: 9,
      x: 93.6078,   
      y: 496.478,   
      width: 150,
      height: 24
    },
    witnessName: {
      page: 9,
      x: 363.36,   
      y: 441.045,   
      width: 121,
      height: 28
    }
  },
  soletrader: {
    // You'll need to fill in these coordinates
    primarySignature: {
      page: 11,
      x: 41,
      y: 368,
      width: 207,
      height: 72
    },
    witnessSignature: {
      page: 11, 
      x: 331,
      y: 368,
      width: 193,
      height: 71
    },
    primaryName: {
      page: 11, 
      x: 81,
      y: 467,
      width: 175,
      height: 21
    },
    witnessName: {
      page: 11, 
      x: 340,
      y: 470,
      width: 192,
      height: 20
    },
    policeCheckNumber: {
      page: 0, 
      x: 153,
      y: 190,
      width: 151,
      height: 20
    },
    visaNumber: {
      page: 0, 
      x: 153,
      y: 170,
      width: 151,
      height: 19
    }
  },
  partnership: {
    // You'll need to fill in these coordinates
    primarySignature: {
      page: 11, // Example - replace with actual
      x: 308,
      y: 336,
      width: 215,
      height: 43
    },
    witnessSignature: {
      page: 11, // Example - replace with actual
      x: 26,
      y: 317,
      width: 226,
      height: 42
    },
    primaryName: {
      page: 11, // Example - replace with actual
      x: 310,
      y: 305,
      width: 215,
      height: 16
    },
    witnessName: {
      page: 11, // Example - replace with actual
      x: 29,
      y: 303,
      width: 215,
      height: 17
    },
    policeCheckNumber: {
      page: 0, // Example - replace with actual
      x: 153,
      y: 185,
      width: 150,
      height: 15
    },
    visaNumber: {
      page: 0, // Example - replace with actual
      x: 153,
      y: 171,
      width: 150,
      height: 13
    }
  },
  company: { // Director_Contractor in backend
    // You'll need to fill in these coordinates
    primarySignature: {
      page: 11, // Example - replace with actual
      x: 304,
      y: 415,
      width: 221,
      height: 50
    },
    secretarySignature: {
      page: 11, // Example - replace with actual
      x: 33,
      y: 410,
      width: 221,
      height: 49
    },
    primaryName: {
      page: 11, // Example - replace with actual
      x: 307,
      y: 370,
      width: 215,
      height: 19
    },
    secretaryName: {
      page: 11, // Example - replace with actual
      x: 29,
      y: 369,
      width: 215,
      height: 19
    },
    policeCheckNumber: {
      page: 0, // Example - replace with actual
      x: 152,
      y: 190,
      width: 150,
      height: 15
    },
    visaNumber: {
      page: 0, // Example - replace with actual
      x: 153,
      y: 170,
      width: 150,
      height: 15
    }
  },
  declinetfn: {
    // Only one signature for this contract type
    signatureField: {
      page: 0, // Example - replace with actual
      x: 109,
      y: 126,
      width: 170,
      height: 54
    }
  }
};

// Contract type titles for display
const CONTRACT_TITLES = {
  employment: "Employment Contract",
  soletrader: "Sole Trader Contract",
  partnership: "Partnership Contract",
  company: "Company Contract",
  declinetfn: "Decline TFN Offer"
};

const UnifiedContract = ({ 
  onComplete,
  onCancel,
  width = 400,
  height = 200,
  containerClassName = "",
  buttonContainerClassName = "mt-4 flex justify-end space-x-3"
}) => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const companylogo = "/static/images/snlpic.png";
  const [showLoadingTransition, setShowLoadingTransition] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [contractData, setContractData] = useState(null);
  const [contractType, setContractType] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfBytes, setPdfBytes] = useState(null);
  const loadInputRef = useRef(false);
  const userData = locationState?.data;
  const userType = locationState?.userType || 'Existing';
  // Signatures state
  const [primarySignature, setPrimarySignature] = useState(null);
  const [secondarySignature, setSecondarySignature] = useState(null);
  
  // Form field states
  const [primaryName, setPrimaryName] = useState('');
  const [secondaryName, setSecondaryName] = useState('');
  const [policeCheckNumber, setPoliceCheckNumber] = useState('');
  const [visaNumber, setVisaNumber] = useState('');
  
  // Validation state
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalPdfBlob, setFinalPdfBlob] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [openContract, setOpenContract] = useState(false);
  
  

  // Get fields configuration based on contract type
  const getFieldsConfig = () => {
    switch(contractType) {
      case 'employment':
        return {
          needsSecondarySignature: true,
          secondarySignatureLabel: 'Witness',
          secondaryNameField: 'Witness Name',
          showPoliceCheck: false,
          showVisa: false
        };
      case 'soletrader':
      case 'partnership':
        return {
          needsSecondarySignature: true,
          secondarySignatureLabel: 'Witness',
          secondaryNameField: 'Witness Name',
          showPoliceCheck: true,
          showVisa: true
        };
      case 'company':
        return {
          needsSecondarySignature: true,
          secondarySignatureLabel: 'Secretary',
          secondaryNameField: 'Secretary Name',
          showPoliceCheck: true,
          showVisa: true
        };
      case 'declinetfn':
      default:
        return {
          needsSecondarySignature: false,
          secondarySignatureLabel: '',
          secondaryNameField: '',
          showPoliceCheck: false,
          showVisa: false
        };
    }
  };
  
  // Check if the form is valid based on contract type
  const isFormValid = () => {
    const config = getFieldsConfig();
    
    // Base validation (primary signature and name always required)
    let isValid = primarySignature && primaryName.trim();
    
    // Add secondary signature validation if needed
    if (config.needsSecondarySignature) {
      isValid = isValid && secondarySignature && secondaryName.trim();
    }
    
    // Add additional fields validation if needed
    if (config.showPoliceCheck) {
      isValid = isValid && policeCheckNumber.trim();
    }
    
    if (config.showVisa) {
      isValid = isValid && visaNumber.trim();
    }
    
    return isValid;
  };
  

const openContractPdf = async () => {
  setTimeout (() => {

    setOpenContract(true);
    }, 100);
   

};




  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    if (isFormValid()) {
      setIsFinalizing(true);
      
      try {
        // Add signatures to PDF client-side
        const signedPdfBlob = await addSignaturesToPdf();
        setFinalPdfBlob(signedPdfBlob);
        setPreviewMode(true);
        setIsFinalizing(false);
      } catch (error) {
        console.error("Error adding signatures to PDF:", error);
        alert("There was an error processing your signatures. Please try again.");
        setIsFinalizing(false);
      }
    } else {
      // Focus on the first missing field
      // This is a simplified version - you may want to expand it to handle all fields
      if (!primaryName.trim()) {
        document.getElementById('primaryName').focus();
      } else if (!primarySignature) {
        document.getElementById('primarySignatureSection').scrollIntoView({ behavior: 'smooth' });
      }
    }
  };









  // Function to add signatures to PDF
  const addSignaturesToPdf = async () => {
    try {
      // Fetch the PDF if we don't have it already
      if (!pdfBytes) {
        const response = await fetch(pdfUrl);
        const pdfBuffer = await response.arrayBuffer();
        setPdfBytes(pdfBuffer);
        
        // Use the fetched buffer
        return processSignatures(pdfBuffer);
      } else {
        // Use the existing buffer
        return processSignatures(pdfBytes);
      }
    } catch (error) {
      console.error("Error in addSignaturesToPdf:", error);
      throw error;
    }
  };

  // Process signatures and fields based on contract type
const processSignatures = async (pdfBuffer) => {
  try {
    const pdfDoc = await PDFDocument.load(pdfBuffer, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();
    const config = getFieldsConfig();
    
    // Safely get coordinates, with error checking
    if (!SIGNATURE_COORDINATES[contractType]) {
      console.error(`No signature coordinates found for contract type: ${contractType}`);
      throw new Error(`Signature coordinates not defined for ${contractType}`);
    }
    
    const coords = SIGNATURE_COORDINATES[contractType];
    console.log("Processing signatures for contract type:", contractType);
    
    // Process based on contract type
    if (contractType === 'declinetfn') {
      // Only process the single signature for Decline TFN
      if (primarySignature && coords.signatureField) {
        const pageIndex = coords.signatureField.page;
        if (pageIndex >= 0 && pageIndex < pages.length) {
          await processSignatureImage(
            primarySignature,
            pdfDoc,
            pages[pageIndex],
            coords.signatureField.x,
            coords.signatureField.y,
            coords.signatureField.width,
            coords.signatureField.height
          );
        } else {
          console.error(`Page index out of bounds: ${pageIndex}. PDF has ${pages.length} pages.`);
        }
      }
    } else {
      // Process primary name
      if (primaryName) {
        const nameField = contractType === 'employment' ? coords.employeeName : coords.primaryName;
        if (nameField && nameField.page >= 0 && nameField.page < pages.length) {
          const namePage = pages[nameField.page];
          
          namePage.drawText(primaryName, {
            x: nameField.x,
            y: nameField.y + (nameField.height / 2) - 6,
            size: 12,
            color: rgb(0, 0, 0)
          });
        } else {
          console.error("Invalid primary name field configuration");
        }
      }
      
      // Process secondary name if needed
      if (config.needsSecondarySignature && secondaryName) {
        let nameField;
        if (contractType === 'employment') {
          nameField = coords.witnessName;
        } else if (contractType === 'company') {
          nameField = coords.secretaryName;
        } else {
          nameField = coords.witnessName;
        }
        
        if (nameField && nameField.page >= 0 && nameField.page < pages.length) {
          const namePage = pages[nameField.page];
          
          namePage.drawText(secondaryName, {
            x: nameField.x,
            y: nameField.y + (nameField.height / 2) - 6,
            size: 12,
            color: rgb(0, 0, 0)
          });
        } else {
          console.error("Invalid secondary name field configuration");
        }
      }
      
      // Process additional fields
      if (config.showPoliceCheck && policeCheckNumber && coords.policeCheckNumber) {
        const field = coords.policeCheckNumber;
        if (field.page >= 0 && field.page < pages.length) {
          const page = pages[field.page];
          
          page.drawText(policeCheckNumber, {
            x: field.x,
            y: field.y + (field.height / 2) - 6,
            size: 10,
            color: rgb(0, 0, 0)
          });
        } else {
          console.error("Invalid police check field configuration");
        }
      }
      
      if (config.showVisa && visaNumber && coords.visaNumber) {
        const field = coords.visaNumber;
        if (field.page >= 0 && field.page < pages.length) {
          const page = pages[field.page];
          
          page.drawText(visaNumber, {
            x: field.x,
            y: field.y + (field.height / 2) - 6,
            size: 10,
            color: rgb(0, 0, 0)
          });
        } else {
          console.error("Invalid visa number field configuration");
        }
      }
      
      // Process primary signature
      if (primarySignature) {
        let sigField;
        if (contractType === 'employment') {
          sigField = coords.employeeSignature;
        } else {
          sigField = coords.primarySignature;
        }
        
        if (sigField && sigField.page >= 0 && sigField.page < pages.length) {
          await processSignatureImage(
            primarySignature,
            pdfDoc,
            pages[sigField.page],
            sigField.x,
            sigField.y,
            sigField.width,
            sigField.height
          );
        } else {
          console.error("Invalid primary signature field configuration");
        }
      }
      
      // Process secondary signature if needed
      if (config.needsSecondarySignature && secondarySignature) {
        let sigField;
        if (contractType === 'employment') {
          sigField = coords.witnessSignature;
        } else if (contractType === 'company') {
          sigField = coords.secretarySignature;
        } else {
          sigField = coords.witnessSignature;
        }
        
        if (sigField && sigField.page >= 0 && sigField.page < pages.length) {
          await processSignatureImage(
            secondarySignature,
            pdfDoc,
            pages[sigField.page],
            sigField.x,
            sigField.y,
            sigField.width,
            sigField.height
          );
        } else {
          console.error("Invalid secondary signature field configuration");
        }
      }
    }
    
    // Save the modified PDF
    const modifiedPdfBytes = await pdfDoc.save({ updateFieldAppearances: false });
    
    // Convert to Blob
    return new Blob([modifiedPdfBytes], { type: 'application/pdf' });
  } catch (error) {
    console.error("Error in processSignatures:", error);
    throw error;
  }
};

// Helper function to process signature images
const processSignatureImage = async (signatureDataUrl, pdfDoc, page, x, y, width, height) => {
  try {
    if (!signatureDataUrl) {
      console.error("No signature data provided");
      return;
    }
    
    // Check if the dataURL is valid
    if (!signatureDataUrl.includes('data:image') && !signatureDataUrl.includes('base64')) {
      console.error("Invalid signature data URL format");
      return;
    }
    
    // Split correctly based on data URL format
    let sigData;
    try {
      // Convert data URL to image
      const dataUrlParts = signatureDataUrl.split(',');
      sigData = dataUrlParts.length > 1 ? dataUrlParts[1] : dataUrlParts[0];
      
      // Quick validation
      if (!sigData || sigData.length === 0) {
        console.error("Empty signature data");
        return;
      }
      
      const binaryString = atob(sigData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      try {
        const sigImage = await pdfDoc.embedPng(bytes);
        
        // Validate image was embedded successfully
        if (!sigImage) {
          console.error("Failed to embed PNG image");
          return;
        }
        
        // Calculate dimensions to maintain aspect ratio
        const aspectRatio = sigImage.height / sigImage.width;
        const sigWidth = width || 150; // Default width if not provided
        const sigHeight = sigWidth * aspectRatio;
        
        // Draw the image on the page
        page.drawImage(sigImage, {
          x: x,
          y: y,
          width: sigWidth,
          height: sigHeight
        });
        
        console.log("Signature image embedded successfully");
      } catch (embedError) {
        console.error("Error embedding PNG:", embedError);
        // Attempt fallback if PNG embedding fails
        // This could happen with certain signature formats
        console.log("Attempting to draw signature as text instead");
        page.drawText("[Signature]", {
          x: x,
          y: y + 10,
          size: 12,
          color: rgb(0, 0, 0)
        });
      }
    } catch (parseError) {
      console.error("Error parsing signature data:", parseError);
      // Fallback if we can't decode the base64
      page.drawText("[Signature]", {
        x: x,
        y: y + 10,
        size: 12, 
        color: rgb(0, 0, 0)
      });
    }
  } catch (error) {
    console.warn("Error processing signature image:", error);
    // Don't rethrow - allow the process to continue even if signature fails
  }
};


    const sendAutomationEmail = async () => {
          try {
              const response = await fetch('/api/react-emails/Submission', {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(contractData)
              });
      
              
              const result = await response.json();
              if (result) {

                alert("Email successfully sent");
              };
            } catch (error) {
            console.error("Error submitting sending email", error);
            alert("There was an error submitting email.");
          }
        };
  // Submit final signed document to the server
  const submitSignedDocument = async () => {
    
    try {
      setIsSubmitting(true);
      
      if (!finalPdfBlob) {
        throw new Error("No signed PDF to submit");
      }
      
      // Create form data with the signed PDF
      const formData = new FormData();
      // Create a timestamp and include it in the filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '');
      formData.append('signedContract', finalPdfBlob, `${contractType}_${contractData[14]}_${timestamp}.pdf`);
      
      // Add metadata as a JSON string
      const updatedUserData = [...contractData];
      updatedUserData[35] = pdfUrl;
      
      // Create metadata object based on contract type
      const metadataObj = {
        primary: {
          name: primaryName,
          timestamp: new Date().toISOString()
        },
        userData: updatedUserData
      };
      
      // Add secondary signature metadata if needed
      if (getFieldsConfig().needsSecondarySignature) {
        metadataObj.secondary = {
          name: secondaryName,
          timestamp: new Date().toISOString()
        };
      }
      
      // Add additional fields if needed
      if (getFieldsConfig().showPoliceCheck) {
        metadataObj.policeCheckNumber = policeCheckNumber;
      }
      
      if (getFieldsConfig().showVisa) {
        metadataObj.visaNumber = visaNumber;
      }
      
      formData.append('metadata', JSON.stringify(metadataObj));
      
      // Submit to backend
      const response = await fetch('/api/finalise/employee', {
        method: 'POST',
        body: formData
      });
      
      
      const result = await response.json();
      
      
      if (result) {
       
        alert("Contract signed successfully!");
        sendAutomationEmail();
        if (onComplete) {
          onComplete(metadataObj);
        } else {

          navigate('/landingprofile', { state: { userType, data: result } });
        }
      } else {

        alert("Contract signing was unsuccessfully!");
      }
    } catch (error) {
      console.error("Error submitting contract signatures:", error);
      alert("There was an error submitting your signatures. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (previewMode) {
      setPreviewMode(false);
      setFinalPdfBlob(null);
    } else if (onCancel) {
      onCancel();
    } else {
      navigate('/landingprofile', { state: { userType, data: userData } });
    }
  };

  const handleBack = () => {
    navigate('/landingprofile', { state: { userType, data: userData } });
  };
  
    
    

  // First useEffect - Fetch data
  useEffect(() => {
    if (loadInputRef.current === false) {
      setShowLoadingTransition(true);
      
      const fetchProfileData = async () => {
        try {
          if (!userData) {
            navigate('/landingprofile');
            return;
          }
          
          const response = await fetch('/api/view_contract_draft', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData)
          });
          
          const result = await response.json();
          
          if (result && result.success) {
            setPdfUrl(result.pdfUrl);
            setContractData(result.data);
            
            
            // Set contract type from the data
            if (result.data && result.data[33]) {
              setContractType(result.data[33]);
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch profile data:', error);
          }
        } finally {
          setShowLoadingTransition(false);
        }
      };
      
      fetchProfileData();
      loadInputRef.current = true;
    }
  }, [userData, navigate]);





  // Second useEffect - Process data once it's available
  useEffect(() => {
    
    if (contractData) {
      setFirstName(contractData[4] || '');
      setPrimaryName(contractData[14] || '');
      
    }
  }, [contractData]);

  // Third useEffect - Fetch PDF content when URL is available
  useEffect(() => {
    if (pdfUrl) {
      const fetchPdf = async () => {
        try {
          const response = await fetch(pdfUrl);
          const pdfBuffer = await response.arrayBuffer();
          setPdfBytes(pdfBuffer);
          
        } catch (error) {
          console.error('Failed to fetch PDF content:', error);
        }
      };
      
      fetchPdf();
    }
  }, [pdfUrl]);
  
  // Render preview of signed contract
  const renderPreview = () => {
  if (!finalPdfBlob) return null;
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#22d3ee' }}>Review Signed Contract</h1>
      </div>
      
      <p className="mb-6" style={{ color: '#ffffff' }}>Please review your signed contract before final submission.</p>
      
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <iframe 
          src={URL.createObjectURL(finalPdfBlob)} 
          className="w-full h-96 border-none"
          title="Signed Contract Preview"
        ></iframe>
      </div>
        
        <div className="flex justify-between mt-6">
          <button 
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
            disabled={isSubmitting}
          >
            Back to Signing
          </button>
          
          <button 
            type="button"
            onClick={submitSignedDocument}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Signed Contract'}
          </button>
        </div>
      </div>
    );
  };
  
  // Render contract content
const renderContractContent = () => {
  if (!contractData) return null;
  
  const config = getFieldsConfig();
  const contractTitle = CONTRACT_TITLES[contractType] || "Contract";
  
  return (
    <div>
      {/* Flex container for the top row */}
      <div className="flex flex-col md:flex-row md:justify-between items-start gap-4 mb-4">
        <h1 className="text-2xl font-bold" style={{ color: '#22d3ee' }}>
          {contractTitle}
        </h1>

        <div className="grid grid-cols-1 md:flex md:flex-row gap-2 shrink-0">
          <button
            onClick={openContractPdf}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Display Contract
          </button>
          
          <a 
            href={pdfUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition inline-block no-underline text-center"
            role="button"
          >
            View PDF
          </a>
        </div>
      </div>

  {/* Paragraph always below */}
  <p className="mb-6" style={{ color: '#ffffff' }}>
    Hello <span className="font-semibold">{firstName}</span>, please review your contract and sign below.
  </p>

      
      {/* Contract content */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4">Contract Terms</h2>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">Name:</p>
              <p className="font-medium">{contractData[14]}</p>
            </div>
            <div>
              <p className="text-gray-600">Position:</p>
              <p className="font-medium">{contractData[13]}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">Start Date:</p>
              <p className="font-medium">{contractData[19]}</p>
            </div>
            <div>
              <p className="text-gray-600">Contract Status:</p>
              <p className="font-medium">{contractData[8]}</p>
            </div>
          </div>
          
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold mb-2">Contract Details</h3>
            <p className="text-gray-700">
              This {contractTitle} is entered into between SNL Commercial Property Services PTY LTD 
              and {contractData[14]}, collectively referred to as "the Parties."
            </p>
          </div>
        </div>
      </div>
      
        { openContract && (
      <div className="pb-6 mt-6"> 
        <ContractViewer
          pdfUrl={pdfUrl}
          height="500px"
          borderRadius="18px"
          onClose={() => setOpenContract(false)}
        />
      </div>
    )}
      
      <div className={`multi-signature-container ${containerClassName}`}>
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Signature sections in a responsive grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Primary signatory section */}
              <div className="primary-signature-section bg-white p-6 rounded-lg shadow" id="primarySignatureSection">
                <h3 className="text-lg font-medium mb-4">Primary Signatory</h3>
                
                <div className="mb-4">
                  <label htmlFor="primaryName" className="block text-sm font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    id="primaryName"
                    value={primaryName}
                    onChange={(e) => setPrimaryName(e.target.value)}
                    className={`w-full px-3 py-2 border rounded ${submitted && !primaryName.trim() ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter your full name"
                  />
                  {submitted && !primaryName.trim() && (
                    <div className="text-red-500 text-sm mt-1">Name is required</div>
                  )}
                </div>
                
                {/* Additional fields for specific contract types */}
                {config.showPoliceCheck && (
                  <div className="mb-4">
                    <label htmlFor="policeCheckNumber" className="block text-sm font-medium mb-1">Police Check Number</label>
                    <input
                      type="text"
                      id="policeCheckNumber"
                      value={policeCheckNumber}
                      onChange={(e) => setPoliceCheckNumber(e.target.value)}
                      className={`w-full px-3 py-2 border rounded ${submitted && config.showPoliceCheck && !policeCheckNumber.trim() ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter Police Check Number"
                    />
                    {submitted && config.showPoliceCheck && !policeCheckNumber.trim() && (
                      <div className="text-red-500 text-sm mt-1">Police Check Number is required</div>
                    )}
                  </div>
                )}
                
                {config.showVisa && (
                  <div className="mb-4">
                    <label htmlFor="visaNumber" className="block text-sm font-medium mb-1">Visa Number</label>
                    <input
                      type="text"
                      id="visaNumber"
                      value={visaNumber}
                      onChange={(e) => setVisaNumber(e.target.value)}
                      className={`w-full px-3 py-2 border rounded ${submitted && config.showVisa && !visaNumber.trim() ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter Visa Number"
                    />
                    {submitted && config.showVisa && !visaNumber.trim() && (
                      <div className="text-red-500 text-sm mt-1">Visa Number is required</div>
                    )}
                  </div>
                )}
                
                <div className="signature-pad-wrapper">
                  <SignaturePad
                    width={width}
                    height={height}
                    onSave={(dataUrl) => {
                      setPrimarySignature(dataUrl);
                      
                    }}
                    onClear={() => {
                      setPrimarySignature(null);
                      
                    }}
                    label="Your Signature"
                    instructionText="Please sign clearly within the box below. Your signature should be complete and legible."
                  />
                  
                  {submitted && !primarySignature && (
                    <div className="text-red-500 text-sm mt-1">Your signature is required</div>
                  )}
                </div>
              </div>
              
              {/* Secondary signature section (witness or secretary) */}
              {config.needsSecondarySignature && (
                <div className="secondary-signature-section bg-white p-6 rounded-lg shadow" id="secondarySignatureSection">
                  <h3 className="text-lg font-medium mb-4">{config.secondarySignatureLabel}</h3>
                  
                  <div className="mb-4">
                    <label htmlFor="secondaryName" className="block text-sm font-medium mb-1">{config.secondaryNameField}</label>
                    <input
                      type="text"
                      id="secondaryName"
                      value={secondaryName}
                      onChange={(e) => setSecondaryName(e.target.value)}
                      className={`w-full px-3 py-2 border rounded ${submitted && !secondaryName.trim() ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder={`Enter ${config.secondaryNameField.toLowerCase()}`}
                    />
                    {submitted && !secondaryName.trim() && (
                      <div className="text-red-500 text-sm mt-1">{config.secondaryNameField} is required</div>
                    )}
                  </div>
                  
                  <div className="signature-pad-wrapper">
                    <SignaturePad
                      width={width}
                      height={height}
                      onSave={(dataUrl) => {
                        setSecondarySignature(dataUrl);
                        
                      }}
                      onClear={() => {
                        setSecondarySignature(null);
                        
                      }}
                      label={`${config.secondarySignatureLabel} Signature`}
                      instructionText={`Please have the ${config.secondarySignatureLabel.toLowerCase()} sign clearly within the box below.`}
                    />
                    
                    {submitted && !secondarySignature && (
                      <div className="text-red-500 text-sm mt-1">{config.secondarySignatureLabel} signature is required</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col md:flex-row space-y-4 pb-8 md:space-y-0 md:space-x-4 justify-between mt-6">
              

              <div className={buttonContainerClassName}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 mr-2"
                  disabled={isFinalizing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded shadow-sm text-sm font-medium text-white ${
                    isFormValid() && !isFinalizing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'
                  }`}
                  disabled={!isFormValid() || isFinalizing}
                >
                  {isFinalizing ? 'Processing...' : 'Preview Signed Document'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
  
  return (
  <>
    <HeaderComponent
      title="Contract"
      backLabel="Back to Hub"
      onBack={handleBack}
      showSearch={false}
    />
    
    {/* Outer: full-width dark background, 50px gap top and bottom */}
    <div 
      className="min-h-screen w-full pt-[50px] pb-[50px] px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#1a1a2e" }}
    >
      {/* Inner: centered content area, capped width */}
      <div style={{ width: "100%", maxWidth: "896px", margin: "0 auto" }}>
        {showLoadingTransition ? (
          <LoadingTransition 
            companylogo={companylogo} 
            message="Preparing your contract for review..."
          />
        ) : previewMode ? (
          renderPreview()
        ) : (
          renderContractContent()
        )}
      </div>
    </div>
  </>
);
};

export default UnifiedContract;